/**
 * Admin Customers RPC definitions
 *
 * Read RPCs for the Customers admin UI (list / detail / members) plus a
 * create-customer action. All endpoints require admin role.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Person } from "@fm/db/frontend";
import { AgentRatePlan, Customer, CustomerRole, CustomerType } from "@fm/db/models/Customer";
import { CustomerMember, CustomerMemberRole } from "@fm/db/models/CustomerMember";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import {
  DatabaseError,
  Forbidden,
  PersonAlreadyExistsError,
  PersonNotMemberError,
  RecordNotFoundError,
  Unauthorized,
  ValidationError,
} from "../errors.ts";

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * Customer enriched with rollup metrics for the admin list/detail views.
 *
 * Extends the base `Customer.json` schema with computed fields:
 * - `installCount`: number of agent installations linked to the customer's onboardings
 * - `mrrCents`: sum of active subscription amounts (cents)
 * - `lifetimeCents`: sum of paid install fees across the customer's installations (cents)
 * - `lastActivityAt`: most recent `updated_at` across the customer's installations
 *
 * @since 1.0.0
 */
export const CustomerWithRollup = Schema.extend(
  Customer.json,
  Schema.Struct({
    installCount: Schema.Int,
    mrrCents: Schema.Int,
    lifetimeCents: Schema.Int,
    lastActivityAt: Schema.NullOr(Schema.DateTimeUtc),
  }),
);

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Input for `get` and `members` — customer id.
 *
 * @since 1.0.0
 */
export const CustomerByIdInput = Schema.Struct({
  id: Schema.NonEmptyTrimmedString,
});

/**
 * Input for `create` — minimum fields needed to seed a customer row.
 *
 * `billingEmail` becomes the primary-contact person's email; `contactName`
 * (optional) names that person, falling back to the customer name when blank.
 *
 * @since 1.0.0
 */
export const CustomerCreateInput = Schema.Struct({
  name: Schema.NonEmptyTrimmedString,
  billingEmail: Schema.NonEmptyTrimmedString,
  contactName: Schema.optional(Schema.NullOr(Schema.String)),
  notes: Schema.optional(Schema.NullOr(Schema.String)),
});

/**
 * Input for `update` — customer id plus any subset of mutable fields.
 *
 * `clerkOrgId` and `stripeCustomerId` are intentionally excluded — those are
 * managed by the Clerk and Stripe integrations and should not be hand-edited
 * via the admin UI.
 *
 * `roles`, `resellerId`, `agentRateAmount` and `agentRatePlan` carry the
 * reseller identity and negotiated per-agent rate established at intake.
 *
 * @since 1.0.0
 */
export const CustomerUpdateInput = Schema.Struct({
  id: Schema.NonEmptyTrimmedString,
  type: Schema.optional(CustomerType),
  name: Schema.optional(Schema.String),
  primaryContactPersonId: Schema.optional(Schema.NullOr(Schema.String)),
  roles: Schema.optional(Schema.Array(CustomerRole)),
  resellerId: Schema.optional(Schema.NullOr(Schema.String)),
  agentRateAmount: Schema.optional(Schema.NullOr(Schema.Int)),
  agentRatePlan: Schema.optional(Schema.NullOr(AgentRatePlan)),
  slug: Schema.optional(Schema.NullOr(Schema.String)),
  notes: Schema.optional(Schema.NullOr(Schema.String)),
  // Physical postal address shown in the CAN-SPAM footer of automated outreach
  // sent on this customer's behalf (CLA-456).
  postalAddress: Schema.optional(Schema.NullOr(Schema.String)),
});

/**
 * Input for `addMember` — link a person to a customer with a role.
 *
 * @since 1.0.0
 */
export const CustomerAddMemberInput = Schema.Struct({
  customerId: Schema.NonEmptyTrimmedString,
  personId: Schema.NonEmptyTrimmedString,
  role: CustomerMemberRole,
});

/**
 * Input for `searchPeople` — substring match on email or name.
 *
 * @since 1.0.0
 */
export const SearchPeopleInput = Schema.Struct({
  query: Schema.String,
});

/**
 * Input for `createWithPerson` — the new primary-contact person plus the
 * customer account identity. Creates customer + person + owner member in a
 * single transaction. Rate fields are optional here; the standing rate is
 * usually set later via `adjustRate`.
 *
 * @since 1.0.0
 */
export const CustomerCreateWithPersonInput = Schema.Struct({
  person: Schema.Struct({
    name: Schema.NonEmptyTrimmedString,
    email: Schema.NonEmptyTrimmedString,
    phone: Schema.optional(Schema.NullOr(Schema.String)),
  }),
  customer: Schema.Struct({
    type: CustomerType,
    name: Schema.NonEmptyTrimmedString,
    roles: Schema.optional(Schema.Array(CustomerRole)),
    resellerId: Schema.optional(Schema.NullOr(Schema.String)),
    agentRateAmount: Schema.optional(Schema.NullOr(Schema.Int)),
    agentRatePlan: Schema.optional(Schema.NullOr(AgentRatePlan)),
    notes: Schema.optional(Schema.NullOr(Schema.String)),
  }),
});

/**
 * Input for `setPrimaryContact` — repoint a customer's primary contact at one
 * of its existing members.
 *
 * @since 1.0.0
 */
export const CustomerSetPrimaryContactInput = Schema.Struct({
  customerId: Schema.NonEmptyTrimmedString,
  personId: Schema.NonEmptyTrimmedString,
});

/**
 * Input for `adjustRate` — update the standing per-agent rate. `agentRatePlan`
 * is carried as a plain string so an unknown plan surfaces as a typed
 * `ValidationError` from the handler rather than a wire-level decode failure.
 *
 * @since 1.0.0
 */
export const CustomerAdjustRateInput = Schema.Struct({
  customerId: Schema.NonEmptyTrimmedString,
  agentRateAmount: Schema.Int,
  agentRatePlan: Schema.String,
});

/**
 * Input for `search` — substring match on a customer's contact email OR phone
 * (case-insensitive) for dedupe.
 *
 * @since 1.0.0
 */
export const CustomerSearchInput = Schema.Struct({
  query: Schema.String,
});

/**
 * One directory-search hit — a customer row enriched with its primary
 * contact's name and email (joined off `primaryContactPersonId`). Both are
 * `null` when the customer has no primary contact. The dedupe UI renders these
 * on the match line, and the superset keeps the other `admin/customers/search`
 * consumers (admin-search, intake-admin) type-checking unchanged.
 *
 * @since 1.0.0
 */
export const CustomerSearchHit = Schema.extend(
  Customer.json,
  Schema.Struct({
    contactName: Schema.NullOr(Schema.String),
    contactEmail: Schema.NullOr(Schema.String),
  }),
);

/**
 * Member row returned by `members` — Person fields plus the role of their
 * `customer_members` row (so the UI can render and edit per-membership role).
 *
 * @since 1.0.0
 */
export const CustomerMemberRow = Schema.extend(
  Person.json,
  Schema.Struct({ membershipRole: CustomerMemberRole }),
);

/**
 * Input for `removeMember` — unlink a person from a customer.
 *
 * @since 1.0.0
 */
export const CustomerRemoveMemberInput = Schema.Struct({
  customerId: Schema.NonEmptyTrimmedString,
  personId: Schema.NonEmptyTrimmedString,
});

/**
 * Input for `updateMemberRole` — change the role of an existing membership.
 *
 * @since 1.0.0
 */
export const CustomerUpdateMemberRoleInput = Schema.Struct({
  customerId: Schema.NonEmptyTrimmedString,
  personId: Schema.NonEmptyTrimmedString,
  role: CustomerMemberRole,
});

/**
 * Input for `updatePerson` — edit a customer member's name, email and phone.
 *
 * `name` and `email` are plain strings (not `NonEmptyTrimmedString`) so a blank
 * name or malformed email comes back from the handler as a field-level
 * `ValidationError` rather than a wire decode failure. Omitted fields are left
 * unchanged; `phone: null` (or blank) clears the phone. The email can only be
 * changed while the person has no Clerk account.
 *
 * @since 1.0.0
 */
export const CustomerUpdatePersonInput = Schema.Struct({
  customerId: Schema.NonEmptyTrimmedString,
  personId: Schema.NonEmptyTrimmedString,
  name: Schema.optional(Schema.String),
  email: Schema.optional(Schema.String),
  phone: Schema.optional(Schema.NullOr(Schema.String)),
});

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Customers RPC group.
 *
 * All endpoints require admin role via AdminMiddleware.
 *
 * @since 1.0.0
 */
export class CustomersRpcs extends RpcGroup.make(
  Rpc.make("list", {
    success: Schema.Array(CustomerWithRollup),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("get", {
    payload: CustomerByIdInput,
    success: CustomerWithRollup,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("members", {
    payload: CustomerByIdInput,
    success: Schema.Array(CustomerMemberRow),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("searchPeople", {
    payload: SearchPeopleInput,
    success: Schema.Array(Person.json),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("create", {
    payload: CustomerCreateInput,
    success: Customer.json,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("createWithPerson", {
    payload: CustomerCreateWithPersonInput,
    success: Customer.json,
    error: Schema.Union(Unauthorized, Forbidden, PersonAlreadyExistsError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("setPrimaryContact", {
    payload: CustomerSetPrimaryContactInput,
    success: Customer.json,
    error: Schema.Union(Unauthorized, Forbidden, PersonNotMemberError, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("adjustRate", {
    payload: CustomerAdjustRateInput,
    success: Customer.json,
    error: Schema.Union(Unauthorized, Forbidden, ValidationError, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("search", {
    payload: CustomerSearchInput,
    success: Schema.Array(CustomerSearchHit),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("update", {
    payload: CustomerUpdateInput,
    success: Customer.json,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError, RecordNotFoundError, ValidationError),
  }).middleware(AdminMiddleware),
  Rpc.make("addMember", {
    payload: CustomerAddMemberInput,
    success: CustomerMember.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("removeMember", {
    payload: CustomerRemoveMemberInput,
    success: Schema.Void,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("updateMemberRole", {
    payload: CustomerUpdateMemberRoleInput,
    success: CustomerMember.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("updatePerson", {
    payload: CustomerUpdatePersonInput,
    success: CustomerMemberRow,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      ValidationError,
      PersonNotMemberError,
      PersonAlreadyExistsError,
      RecordNotFoundError,
      DatabaseError,
    ),
  }).middleware(AdminMiddleware),
).prefix("admin/customers/") {}
